import { ChangelogEntry, ChangelogEntryType, ChangelogService } from '@/types';
import { X } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useChangelog } from '../../zustand/Zustand';

// ─── Shared constants ────────────────────────────────────────────────────────

const serviceBadge: Record<ChangelogService, string> = {
  frontend: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  backend: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  processes: 'bg-green-500/20 text-green-300 border-green-500/30',
};

const typeBadge: Record<ChangelogEntryType, string> = {
  feature: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  bugfix: 'bg-red-500/20 text-red-300 border-red-500/30',
  improvement: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
};

const typeLabel: Record<ChangelogEntryType, string> = {
  feature: 'Features',
  improvement: 'Improvements',
  bugfix: 'Bug Fixes',
};

const typeOrder: ChangelogEntryType[] = ['feature', 'improvement', 'bugfix'];

function renderInline(text: string, keyPrefix: string): React.ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <strong key={`${keyPrefix}-b${i}`} className="text-white font-semibold">{part}</strong>
      : <React.Fragment key={`${keyPrefix}-t${i}`}>{part}</React.Fragment>
  );
}

function renderDescription(text: string): React.ReactNode {
  // Split on blank-line paragraph breaks; within each block, lines beginning
  // with "- " form a bulleted list, other lines form prose with <br/> breaks.
  const blocks = text.split(/\n{2,}/);
  return blocks.map((block, bi) => {
    const lines = block.split('\n');
    const nodes: React.ReactNode[] = [];
    let i = 0;
    while (i < lines.length) {
      if (lines[i].startsWith('- ')) {
        const items: string[] = [];
        while (i < lines.length && lines[i].startsWith('- ')) {
          items.push(lines[i].slice(2));
          i++;
        }
        nodes.push(
          <ul key={`b${bi}-ul${nodes.length}`} className="list-disc pl-5 space-y-1 my-2">
            {items.map((item, ii) => (
              <li key={ii}>{renderInline(item, `b${bi}-l${ii}`)}</li>
            ))}
          </ul>
        );
      } else {
        const proseLines: string[] = [];
        while (i < lines.length && !lines[i].startsWith('- ')) {
          proseLines.push(lines[i]);
          i++;
        }
        nodes.push(
          <React.Fragment key={`b${bi}-p${nodes.length}`}>
            {proseLines.map((line, li) => (
              <React.Fragment key={li}>
                {li > 0 && <br />}
                {renderInline(line, `b${bi}-p${li}`)}
              </React.Fragment>
            ))}
          </React.Fragment>
        );
      }
    }
    return (
      <p key={bi} className={bi > 0 ? 'mt-3' : ''}>
        {nodes}
      </p>
    );
  });
}

function groupByType(entries: ChangelogEntry[]): { type: ChangelogEntryType; items: ChangelogEntry[] }[] {
  const map = new Map<ChangelogEntryType, ChangelogEntry[]>();
  for (const entry of entries) {
    if (!map.has(entry.type)) map.set(entry.type, []);
    map.get(entry.type)!.push(entry);
  }
  return typeOrder
    .filter((t) => map.has(t))
    .map((t) => ({ type: t, items: map.get(t)! }));
}

// ─── Shared entry card ───────────────────────────────────────────────────────

interface EntryCardProps {
  entry: ChangelogEntry;
  showDate?: boolean;
  showNewBadge?: boolean;
}

const EntryCard: React.FC<EntryCardProps> = ({ entry, showDate, showNewBadge }) => (
  <div className={`border rounded-lg p-4 ${entry.seen ? 'border-fp-border/50 bg-fp-card/30' : 'border-indigo-500/40 bg-indigo-900/10'}`}>
    <div className="flex items-start gap-2 flex-wrap mb-2">
      {entry.services.map((svc) => (
        <span key={svc} className={`text-[10px] font-medium px-2 py-0.5 rounded border ${serviceBadge[svc]}`}>
          {svc}
        </span>
      ))}
      {showDate && (
        <span className="text-[10px] text-gray-500 ml-auto">
          {new Date(entry.releasedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
      )}
      {showNewBadge && !entry.seen && (
        <span className="text-[10px] font-medium px-2 py-0.5 rounded border bg-indigo-500/20 text-indigo-300 border-indigo-500/30 ml-auto">
          new
        </span>
      )}
    </div>
    <p className="text-sm font-semibold text-white mb-1">{entry.title}</p>
    <div className="text-xs text-gray-400 leading-relaxed">{renderDescription(entry.description)}</div>
  </div>
);

// ─── Shared overlay wrapper ──────────────────────────────────────────────────

interface OverlayProps {
  onClose: () => void;
  maxWidth?: string;
  maxHeight?: string;
  children: React.ReactNode;
}

const Overlay: React.FC<OverlayProps> = ({ onClose, maxWidth = 'max-w-xl', maxHeight = 'max-h-[85vh]', children }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[10100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div ref={ref} className={`relative z-10 bg-fp-bg border border-fp-border rounded-xl shadow-2xl w-full ${maxWidth} mx-4 flex flex-col ${maxHeight}`}>
        {children}
      </div>
    </div>
  );
};

// ─── "What's New" modal (unseen entries only) ────────────────────────────────

interface ChangelogModalProps {
  entries: ChangelogEntry[];
  onClose: () => void;
}

export const ChangelogModal: React.FC<ChangelogModalProps> = ({ entries, onClose }) => {
  const { t } = useTranslation(['common']);
  const groups = groupByType(entries);

  return (
    <Overlay onClose={onClose} maxWidth="max-w-lg" maxHeight="max-h-[80vh]">
      <div className="flex items-center justify-between px-5 py-4 border-b border-fp-border shrink-0">
        <div>
          <h2 className="text-lg font-semibold text-white">What's New</h2>
          <p className="text-xs text-gray-400 mt-0.5">{entries.length} update{entries.length !== 1 ? 's' : ''} since your last visit</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors" aria-label={t('common:buttons.close')}>
          <X size={20} />
        </button>
      </div>

      <div className="overflow-y-auto flex-1 px-5 py-4 space-y-6">
        {groups.map(({ type, items }) => (
          <div key={type}>
            <p className="mb-3"><span className={`text-xs font-semibold uppercase tracking-widest px-2 py-0.5 rounded border ${typeBadge[type]}`}>{typeLabel[type]}</span></p>
            <div className="space-y-3">
              {items.map((entry) => <EntryCard key={entry.entryId} entry={entry} showDate />)}
            </div>
          </div>
        ))}
      </div>

      <div className="px-5 py-4 border-t border-fp-border shrink-0">
        <button onClick={onClose} className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors">
          Got it!
        </button>
      </div>
    </Overlay>
  );
};

// ─── Full changelog dialog (all entries, from store) ─────────────────────────

interface ChangelogDialogProps {
  onClose: () => void;
}

const ChangelogDialog: React.FC<ChangelogDialogProps> = ({ onClose }) => {
  const { t } = useTranslation(['common']);
  const { entries } = useChangelog();
  const groups = groupByType(entries);

  return (
    <Overlay onClose={onClose}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-fp-border shrink-0">
        <h2 className="text-lg font-semibold text-white">Changelog</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors" aria-label={t('common:buttons.close')}>
          <X size={20} />
        </button>
      </div>

      <div className="overflow-y-auto flex-1 px-5 py-4 space-y-6">
        {groups.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">No changelog entries yet.</p>
        )}
        {groups.map(({ type, items }) => (
          <div key={type}>
            <p className="mb-3"><span className={`text-xs font-semibold uppercase tracking-widest px-2 py-0.5 rounded border ${typeBadge[type]}`}>{typeLabel[type]}</span></p>
            <div className="space-y-3">
              {items.map((entry) => <EntryCard key={entry.entryId} entry={entry} showNewBadge />)}
            </div>
          </div>
        ))}
      </div>
    </Overlay>
  );
};

export default ChangelogDialog;
